/** Definicja nawigacji bocznej wraz z widocznością zależną od roli. */

import {
  Blocks,
  Building2,
  CalendarDays,
  ClipboardCheck,
  DoorOpen,
  GraduationCap,
  LayoutDashboard,
  MessageSquareWarning,
  Music4,
  Settings,
  Users,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { ModuleSlug, User, UserRole } from '@/api/types'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Role widzące pozycję. Pusta lista oznacza „wszyscy zalogowani”. */
  roles?: UserRole[]
  /** Pozycja wyłącznie dla konta serwisowego wykonawcy. */
  service?: boolean
  /**
   * Moduł, którego dotyczy pozycja. Wyłączony moduł znika z menu.
   *
   * Sekcje administracyjne (panel, ustawienia, konta, oddziały, zgłoszenia)
   * świadomie nie mają modułu — szkoła nie może odciąć sobie zarządzania
   * własnym systemem.
   */
  module?: ModuleSlug
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Panel główny', icon: LayoutDashboard },
  { to: '/uczniowie', label: 'Uczniowie', icon: Music4, module: 'uczniowie' },
  // Nauczyciel nie przegląda kadry — swój profil otwiera z Ustawień.
  {
    to: '/nauczyciele',
    label: 'Nauczyciele',
    icon: GraduationCap,
    roles: ['OWNER', 'ADMIN'],
    module: 'nauczyciele',
  },
  { to: '/grafik', label: 'Grafik lekcji', icon: CalendarDays, module: 'grafik' },
  { to: '/obecnosci', label: 'Obecności', icon: ClipboardCheck, module: 'obecnosci' },
  { to: '/rozliczenia', label: 'Rozliczenia', icon: Wallet, module: 'rozliczenia' },
  { to: '/sale', label: 'Sale', icon: DoorOpen, module: 'sale' },
  { to: '/oddzialy', label: 'Oddziały', icon: Building2, roles: ['OWNER'] },
  { to: '/konta', label: 'Konta', icon: Users, roles: ['OWNER', 'ADMIN'] },
  // Zgłoszenia to kolejka pracy nad aplikacją, nie sprawa szkoły — widzi je
  // wyłącznie konto serwisowe wykonawcy.
  { to: '/zgloszenia', label: 'Zgłoszenia', icon: MessageSquareWarning, service: true },
  // Moduły też należą do wykonawcy: włączenie funkcji to część wdrożenia,
  // a nie codzienna decyzja szkoły.
  { to: '/moduly', label: 'Moduły', icon: Blocks, service: true },
  { to: '/ustawienia', label: 'Ustawienia', icon: Settings },
]

/**
 * Pozycje menu dla użytkownika.
 *
 * `enabledModules === null` znaczy „stan modułów jeszcze nie dotarł”; wtedy
 * pokazujemy komplet, żeby menu nie doskakiwało przy każdym wejściu.
 */
export function visibleNavItems(
  user: User | null | undefined,
  enabledModules: ReadonlySet<ModuleSlug> | null = null,
): NavItem[] {
  if (!user) return []
  return NAV_ITEMS.filter((item) => {
    if (item.module && enabledModules !== null && !enabledModules.has(item.module)) return false
    // Pozycja serwisowa nie zależy od roli — konto serwisowe ma rolę
    // właścicielki, więc sam filtr po roli pokazałby ją całej administracji.
    if (item.service) return user.is_service
    return !item.roles || item.roles.includes(user.role)
  })
}
